import { CachePolicy } from "contentstack";
import { ConfigStack } from "./config.client.js";
import { unstable_cache } from "next/cache";
import { DEFAULT_FIELD_EXCEPTIONS } from "./business.lib.js";
const ConfigContentType = {
  ConfigurationSetting: "Configuration_Setting",
  FeatureToggle: "Feature_Toggle",
  ServiceConfiguration: "Service_Configuration",
  InternalLinkConfig: "costco_internal_domains",
  ModuleSettings: "Module_Settings"
};
var ServiceConfigurationCSTitleEnums = /* @__PURE__ */ ((ServiceConfigurationCSTitleEnums2) => {
  ServiceConfigurationCSTitleEnums2["GET_PAYMENT_SESSION"] = "getPaymentSession";
  ServiceConfigurationCSTitleEnums2["GET_SOK_PAYMENT_SESSION"] = "getSokPaymentSession";
  ServiceConfigurationCSTitleEnums2["POST_AJAX_PAYMENT_METHOD_UPDATE_CMD"] = "AjaxPaymentMethodUpdateCmd";
  ServiceConfigurationCSTitleEnums2["POST_CREATE_ENTERPRISE_PAYMENT_TOKEN"] = "CreateEnterprisePaymentToken";
  ServiceConfigurationCSTitleEnums2["CostcoURLExtension"] = "Costco URL Extension";
  return ServiceConfigurationCSTitleEnums2;
})(ServiceConfigurationCSTitleEnums || {});
const _getClientConfigurationSettings = async () => {
  if (!ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION) {
    const errorString = "CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION is not defined";
    throw new Error(errorString);
  }
  ConfigStack.setCachePolicy(CachePolicy.CACHE_THEN_NETWORK);
  try {
    const result = await ConfigStack.stack.ContentType(ConfigContentType.ConfigurationSetting).Entry(
      ConfigStack.env.CONTENT_ENTRY_ID_CLIENT_CONFIGURATION ?? ""
    ).language(
      ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION
    ).toJSON().fetch();
    if (process.env.LOG_CONTENTSTACK_CALLS === "true") {
      console.log(
        `Config stack data fetched successfully using getClientConfigurationSettings with
                        contentType: ${ConfigContentType.ConfigurationSetting},
                        entry: ${ConfigStack.env.CONTENT_ENTRY_ID_CLIENT_CONFIGURATION},
                        language: ${ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION}
                        resulting in data: ${typeof result === "object" && JSON.stringify(result)}`
      );
    }
    return result;
  } catch (err) {
    const errorString = `An error occurred in Config stack getClientConfigurationSettings with
                contentType: ${ConfigContentType.ConfigurationSetting},
                entry: ${ConfigStack.env.CONTENT_ENTRY_ID_CLIENT_CONFIGURATION},
                language: ${ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION}
                resulting in error: ${typeof err === "object" && JSON.stringify(err)}`;
    console.error(errorString);
    throw new Error(errorString);
  }
};
const cached_getClientConfigurationSettings = unstable_cache(
  _getClientConfigurationSettings,
  ["digital_getClientConfigurationSettings"],
  { revalidate: 1800 }
);
const getClientConfigurationSettings = async () => {
  const fn = typeof window === "undefined" ? cached_getClientConfigurationSettings : _getClientConfigurationSettings;
  return fn();
};
const _getSiteConfigurationSettings = async () => {
  if (!process.env.NEXT_PUBLIC_CONTENT_ENTRY_ID_SITE_CONFIGURATION) {
    const errorString = "NEXT_PUBLIC_CONTENT_ENTRY_ID_SITE_CONFIGURATION is not defined";
    throw new Error(errorString);
  }
  ConfigStack.setCachePolicy(CachePolicy.CACHE_THEN_NETWORK);
  try {
    const result = await ConfigStack.stack.ContentType(ConfigContentType.ConfigurationSetting).Entry(
      process.env.NEXT_PUBLIC_CONTENT_ENTRY_ID_SITE_CONFIGURATION ?? ""
    ).language(
      ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION
    ).toJSON().fetch();
    if (process.env.LOG_CONTENTSTACK_CALLS === "true") {
      console.log(
        `Config stack data fetched successfully using getClientConfigurationSettings with
                        contentType: ${ConfigContentType.ConfigurationSetting},
                        entry: ${process.env.NEXT_PUBLIC_CONTENT_ENTRY_ID_SITE_CONFIGURATION},
                        language: ${ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION}
                        resulting in data: ${typeof result === "object" && JSON.stringify(result)}`
      );
    }
    return result;
  } catch (err) {
    const errorString = `An error occurred in Config stack getClientConfigurationSettings with
                contentType: ${ConfigContentType.ConfigurationSetting},
                entry: ${process.env.NEXT_PUBLIC_CONTENT_ENTRY_ID_SITE_CONFIGURATION},
                language: ${ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION}
                resulting in error: ${typeof err === "object" && JSON.stringify(err)}`;
    console.error(errorString);
    throw new Error(errorString);
  }
};
const cached_getSiteConfigurationSettings = unstable_cache(
  _getSiteConfigurationSettings,
  ["digital_getSiteConfigurationSettings"],
  { revalidate: 1800 }
);
const getSiteConfigurationSettings = async () => {
  const fn = typeof window === "undefined" ? cached_getSiteConfigurationSettings : _getSiteConfigurationSettings;
  return fn();
};
const _getConfigurationSettingByConfigKey = async (contentType, configKey) => {
  ConfigStack.setCachePolicy(CachePolicy.CACHE_THEN_NETWORK);
  try {
    const result = await ConfigStack.stack.ContentType(contentType).Query().where("configkey", configKey).language(
      ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION ?? ""
    ).toJSON().findOne();
    if (process.env.LOG_CONTENTSTACK_CALLS === "true") {
      console.log(
        `Config stack data fetched successfully using getConfigurationSettingByConfigKey with
                contentType: ${contentType},
                configKey: ${configKey},
                resulting in data: ${typeof result === "object" && JSON.stringify(result)}`
      );
    }
    return result;
  } catch (err) {
    const errorString = `An error occurred in Config stack getConfigurationSettingByConfigKey with 
        contentType: ${contentType},
        configKey: ${configKey},
        resulting in error: ${typeof err === "object" && JSON.stringify(err)}`;
    console.error(errorString);
    throw new Error(errorString);
  }
};
const cached_getConfigurationSettingByConfigKey = unstable_cache(
  _getConfigurationSettingByConfigKey,
  ["digital_getConfigurationSettingByConfigKey"],
  { revalidate: 1800 }
);
const getConfigurationSettingByConfigKey = async (contentType, configKey) => {
  const fn = typeof window === "undefined" ? cached_getConfigurationSettingByConfigKey : _getConfigurationSettingByConfigKey;
  return fn(contentType, configKey);
};
const _getConfigurationSettingByDevelopmentKey = async (contentType, developmentKey) => {
  ConfigStack.setCachePolicy(CachePolicy.CACHE_THEN_NETWORK);
  try {
    const result = await ConfigStack.stack.ContentType(contentType).Query().where("development_key", developmentKey).language(
      process.env.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION ?? ""
    ).toJSON().findOne();
    if (process.env.LOG_CONTENTSTACK_CALLS === "true") {
      console.log(
        `Config stack data fetched successfully using getConfigurationSettingByDevelopmentKey with
            contentType: ${contentType},
            developmentKey: ${developmentKey},
            language: ${process.env.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION},
            resulting in data: ${typeof result === "object" && JSON.stringify(result)}`
      );
    }
    return result;
  } catch (err) {
    const errorString = `An error occurred in Config stack getConfigurationSettingByDevelopmentKey with 
            contentType: ${contentType},
            developmentKey: ${developmentKey},
            resulting in error: ${typeof err === "object" && JSON.stringify(err)}`;
    console.error(errorString);
    throw new Error(errorString);
  }
};
const cached_getConfigurationSettingByDevelopmentKey = unstable_cache(
  _getConfigurationSettingByDevelopmentKey,
  ["digital_getConfigurationSettingByDevelopmentKey"],
  { revalidate: 1800 }
);
const getConfigurationSettingByDevelopmentKey = async (contentType, developmentKey) => {
  const fn = typeof window === "undefined" ? cached_getConfigurationSettingByDevelopmentKey : _getConfigurationSettingByDevelopmentKey;
  return fn(contentType, developmentKey);
};
const _getMemberStatusServiceConfiguration = async () => {
  ConfigStack.setCachePolicy(CachePolicy.CACHE_THEN_NETWORK);
  try {
    const result = await ConfigStack.stack.ContentType(ConfigContentType.ServiceConfiguration).Entry("blt7f418886c46de516").language(
      ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION ?? ""
    ).toJSON().fetch();
    if (process.env.LOG_CONTENTSTACK_CALLS === "true") {
      console.log(
        `Config stack data fetched successfully using getMemberStatusServiceConfiguration with
                contentType: ${ConfigContentType.ServiceConfiguration},
                entry: blt7f418886c46de516,
                language: ${ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION},
                resulting in data: ${typeof result === "object" && JSON.stringify(result)}`
      );
    }
    return result;
  } catch (err) {
    const errorString = `An error occurred in Config stack getMemberStatusServiceConfiguration with 
                contentType: ${ConfigContentType.ServiceConfiguration},
                entry: blt7f418886c46de516,
                language: ${ConfigStack.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION},
                resulting in error: ${typeof err === "object" && JSON.stringify(err)}`;
    console.error(errorString);
    throw new Error(errorString);
  }
};
const cached_getMemberStatusServiceConfiguration = unstable_cache(
  _getMemberStatusServiceConfiguration,
  ["digital_getMemberStatusServiceConfiguration"],
  { revalidate: 1800 }
);
const getMemberStatusServiceConfiguration = async () => {
  const fn = typeof window === "undefined" ? cached_getMemberStatusServiceConfiguration : _getMemberStatusServiceConfiguration;
  return fn();
};
const _getMembershipTypesConfiguration = async () => {
  return await getConfigurationSettingByConfigKey(
    ConfigContentType.ConfigurationSetting,
    "membershipTypes"
  );
};
const cached_getMembershipTypesConfiguration = unstable_cache(
  _getMembershipTypesConfiguration,
  ["digital_getMembershipTypesConfiguration"],
  { revalidate: 1800 }
);
const getMembershipTypesConfiguration = async () => {
  const fn = typeof window === "undefined" ? cached_getMembershipTypesConfiguration : _getMembershipTypesConfiguration;
  return fn();
};
const _getServiceConfigurationSettingsByTitles = async (contentType, titles, locale, enableLivePreview = false) => {
  ConfigStack.setCachePolicy(CachePolicy.CACHE_THEN_NETWORK);
  try {
    const result = await ConfigStack.stack.ContentType(contentType).Query().containedIn("title", titles).except(DEFAULT_FIELD_EXCEPTIONS).toJSON().find();
    if (process.env.LOG_CONTENTSTACK_CALLS === "true") {
      console.log(
        `Business stack data fetched successfully at getServiceConfigurationSettingsByTitles with
          contentType: ${contentType},
          titles: ${JSON.stringify(titles)},
          locale: ${locale},
          enableLivePreview: ${enableLivePreview}
          resulting in data: ${typeof result === "object" && JSON.stringify(result)}`
      );
    }
    return result;
  } catch (err) {
    const errorString = `An error occurred in Business stack getServiceConfigurationSettingsByTitles with
          contentType: ${contentType},
          title: ${JSON.stringify(titles)},
          locale: ${locale},
          enableLivePreview: ${enableLivePreview}
          resulting in error: ${typeof err === "object" && JSON.stringify(err)}`;
    console.error(errorString);
    throw new Error(errorString);
  }
};
const cached_getServiceConfigurationSettingsByTitles = unstable_cache(
  _getServiceConfigurationSettingsByTitles,
  ["digital_getServiceConfigurationSettingsByTitles"],
  { revalidate: 1800 }
);
const getServiceConfigurationSettingsByTitles = async (contentType, titles, locale, enableLivePreview = false) => {
  const fn = typeof window === "undefined" ? cached_getServiceConfigurationSettingsByTitles : _getServiceConfigurationSettingsByTitles;
  return fn(contentType, titles, locale, enableLivePreview);
};
export {
  ConfigContentType,
  ServiceConfigurationCSTitleEnums,
  getClientConfigurationSettings,
  getConfigurationSettingByConfigKey,
  getConfigurationSettingByDevelopmentKey,
  getMemberStatusServiceConfiguration,
  getMembershipTypesConfiguration,
  getServiceConfigurationSettingsByTitles,
  getSiteConfigurationSettings
};
